import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  extractKeywordsByAsin,
  saveExtractToFirebase,
  KeywordByAsinRow,
  estimateSalesToFirstPage,
} from "../../services/jungleScout";
import { getSingleData } from "../../services/service";
import { RoutesUrl } from "../../common/consts/Routes";
import { TranslationPrefixes } from "../../consts/translationPrefixes";
import { CredentialsData } from "../CredentialsForm/CredentialsForm";
import CardComponent from "../shared/CardComponent";
import TableComponent, { Column } from "../shared/TableComponent";

const MARKETPLACE_USA = "us";

export default function AmazonExtract() {
  const { t } = useTranslation();
  const history = useHistory();
  const productDataService = TranslationPrefixes.productDataService;
  const module = "modules.amazonExtract.";
  const [asin, setAsin] = useState("");
  const [marketplace, setMarketplace] = useState(MARKETPLACE_USA);
  const [credentials, setCredentials] = useState<CredentialsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keywords, setKeywords] = useState<KeywordByAsinRow[]>([]);

  useEffect(() => {
    getSingleData("amazonCredentials").then((data) => {
      if (data) {
        const raw = data as unknown as Record<string, string | undefined>;
        setCredentials({
          api_key_name: raw.api_key_name ?? "",
          api_key: raw.api_key ?? ""
        });
      }
    });
  }, []);

  const handleExtract = async () => {
    const trimmed = asin?.trim();
    if (!trimmed) {
      toast.warn(t(module + "asinPlaceholder"));
      return;
    }
    const keyName = credentials?.api_key_name?.trim();
    const key = credentials?.api_key?.trim();
    if (!keyName || !key) {
      toast.warn(t(productDataService + "credentialsRequired"));
      history.push(RoutesUrl.credentialsForm);
      return;
    }
    setLoading(true);
    setKeywords([]);
    try {
      const result = await extractKeywordsByAsin(trimmed, marketplace, keyName, key);
      if (result.errors?.length) {
        toast.error(result.errors[0].detail || t(module + "error"));
        return;
      }
      const data = result.data || [];
      setKeywords(data);
      if (data.length === 0) {
        toast.info(t(module + "noData"));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      const is403 = msg.includes("invalid") || msg.includes("Forbidden") || msg.includes("403");
      toast.error(is403 ? t(productDataService + "invalidCredentials") : (msg || t(module + "error")));
      if (is403) {
        history.push(RoutesUrl.credentialsForm);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToDb = async () => {
    if (keywords.length === 0) return;
    setSaving(true);
    try {
      await saveExtractToFirebase(asin.trim(), marketplace, keywords);
      toast.success(t(module + "saved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t(module + "error"));
    } finally {
      setSaving(false);
    }
  };

  const formatNumber = (v: number | null | undefined) =>
    v != null ? String(v) : "—";
  const formatCurrency = (v: number | null | undefined) =>
    v != null ? `$${Number(v).toFixed(2)}` : "—";

  const columns: Column[] = [
    {
      header: t(module + "keyword"),
      accessor: "attributes.name",
      cell: (_, row) => (
        <Typography variant="body2">
          {row?.attributes?.name ?? "—"}
        </Typography>
      ),
    },
    {
      header: t(module + "organicRank"),
      accessor: "attributes.organic_rank",
      cell: (_, row) => (
        <Typography variant="body2">{formatNumber(row?.attributes?.organic_rank)}</Typography>
      ),
    },
    {
      header: t(module + "sponsoredRank"),
      accessor: "attributes.sponsored_rank",
      cell: (_, row) => (
        <Typography variant="body2">{formatNumber(row?.attributes?.sponsored_rank)}</Typography>
      ),
    },
    {
      header: t(module + "overallRank"),
      accessor: "attributes.overall_rank",
      cell: (_, row) => (
        <Typography variant="body2">{formatNumber(row?.attributes?.overall_rank)}</Typography>
      ),
    },
    {
      header: t(module + "salesToFirstPage"),
      accessor: "salesToFirstPage",
      cell: (data) => (
        <Typography variant="body2">{formatNumber(data)}</Typography>
      ),
    },
    {
      header: t(module + "ppcBidFirstPage"),
      accessor: "attributes.ppc_bid_exact",
      cell: (_, row) => (
        <Typography variant="body2">{formatCurrency(row?.attributes?.ppc_bid_exact)}</Typography>
      ),
    },
    {
      header: t(module + "ppcBidBroad"),
      accessor: "attributes.ppc_bid_broad",
      cell: (_, row) => (
        <Typography variant="body2">{formatCurrency(row?.attributes?.ppc_bid_broad)}</Typography>
      ),
    },
    {
      header: t(module + "searchVolumeExact"),
      accessor: "attributes.monthly_search_volume_exact",
      cell: (_, row) => (
        <Typography variant="body2">{formatNumber(row?.attributes?.monthly_search_volume_exact)}</Typography>
      ),
    },
  ];

  const tableData = keywords.map((k) => ({
    ...k,
    "attributes.name": k.attributes?.name,
    "attributes.organic_rank": k.attributes?.organic_rank,
    "attributes.sponsored_rank": k.attributes?.sponsored_rank,
    "attributes.overall_rank": k.attributes?.overall_rank,
    salesToFirstPage: estimateSalesToFirstPage(k.attributes),
    "attributes.ppc_bid_exact": k.attributes?.ppc_bid_exact,
    "attributes.ppc_bid_broad": k.attributes?.ppc_bid_broad,
    "attributes.monthly_search_volume_exact":
      k.attributes?.monthly_search_volume_exact,
  }));

  return (
    <CardComponent title={t(productDataService + "headline")} isLoading={loading}>
      <Box>
        <Grid container spacing={2} alignItems="flex-end" style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label={t(module + "asinLabel")}
              placeholder={t(module + "asinPlaceholder")}
              value={asin}
              onChange={(e) => setAsin(e.target.value)}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="marketplace-label">
                {t(module + "marketplaceLabel")}
              </InputLabel>
              <Select
                labelId="marketplace-label"
                value={marketplace}
                onChange={(e) => setMarketplace(e.target.value as string)}
              >
                <MenuItem value="us">USA</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExtract}
              disabled={loading}
              fullWidth
            >
              {loading ? t(module + "extracting") : t(module + "extract")}
            </Button>
          </Grid>
        </Grid>

        {keywords.length > 0 && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSaveToDb}
                disabled={saving}
              >
                {t(module + "saveToDb")}
              </Button>
            </Box>
            <TableComponent
              data={tableData}
              columns={columns}
              perPage={10}
              searchKeys={["attributes.name"]}
            />
          </>
        )}

        {!loading && keywords.length === 0 && asin.trim() && (
          <Typography color="textSecondary" style={{ marginTop: 16 }}>
            {t(module + "noData")}
          </Typography>
        )}
      </Box>
    </CardComponent>
  );
}
